// src/index.tsx
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';

const savedTheme = localStorage.getItem('criclocal-theme') || 'dark';
document.body.setAttribute('data-theme', savedTheme);

const style = document.createElement('style');
style.textContent = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght,SOFT,WONK@9..144,400;9..144,500;9..144,600;9..144,700;9..144,900&family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');

  /* ───────────────────────── Design Tokens ─────────────────────────
     Direction: Editorial sport-luxe.
     Wisden × Linear × Loro Piana. Deep forest, champagne, ink, cream parchment.
     Hairlines. Smallcaps. Restrained motion.
  */
  :root {
    --font-display: 'Fraunces', 'Playfair Display', ui-serif, serif;
    --font-body: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
    --font-mono: 'JetBrains Mono', ui-monospace, monospace;

    --radius-xs: 6px;
    --radius-sm: 10px;
    --radius-md: 14px;
    --radius-lg: 20px;
    --radius-xl: 28px;
    --radius-pill: 999px;

    --duration-fast: 180ms;
    --duration-normal: 320ms;
    --duration-slow: 600ms;
    --ease-out-expo: cubic-bezier(0.16, 1, 0.3, 1);
    --ease-spring: cubic-bezier(0.34, 1.56, 0.64, 1);

    --text-hero:    clamp(2.6rem, 1.4rem + 5.6vw, 6.75rem);
    --text-display: clamp(2rem,   1.2rem + 3.2vw, 4rem);
    --text-h1:      clamp(1.6rem, 1.1rem + 1.6vw, 2.4rem);
    --text-h2:      clamp(1.2rem, 0.95rem + 0.7vw, 1.55rem);
    --text-body:    clamp(0.95rem, 0.88rem + 0.2vw, 1.05rem);

    --tracking-tight: -0.025em;
    --tracking-tighter: -0.04em;
    --tracking-wide: 0.08em;
    --tracking-eyebrow: 0.22em;

    --space-section: clamp(3rem, 2rem + 4vw, 7rem);

    /* Brand — muted, considered */
    --brand-ink:       #0B100E;
    --brand-forest:    #2E6B4A;   /* primary, replaces neon grass */
    --brand-forest-2:  #3A8259;   /* hover / highlight */
    --brand-moss:      #1F4A33;   /* deep */
    --brand-champagne: #C9A961;   /* replaces shouty gold */
    --brand-brass:     #A88847;
    --brand-clay:      #B0533A;
    --brand-bone:      #E8E4D8;
    --brand-parchment: #F2EEE4;
    --brand-stone:     #93847A;

    --signal-live:   #C0392B;  /* deep red, not neon */
    --signal-good:   #3A8259;
    --signal-info:   #4A6FA5;
  }

  /* ───────────────────────── Dark — Ink editorial ───────────────────────── */
  [data-theme="dark"] {
    --bg-primary: #0B100E;
    --bg-canvas: radial-gradient(140% 90% at 50% -10%, #122019 0%, #0B100E 55%, #07100B 100%);
    --bg-card: rgba(255, 252, 240, 0.025);
    --bg-card-solid: #131A16;
    --bg-surface: rgba(255, 252, 240, 0.04);
    --bg-surface-alt: #1A2620;
    --bg-elevated: rgba(255, 252, 240, 0.05);

    --glass-bg: rgba(11, 16, 14, 0.55);
    --glass-border: rgba(232, 228, 216, 0.08);
    --glass-highlight: rgba(232, 228, 216, 0.04);

    --border-color: rgba(232, 228, 216, 0.09);
    --border-strong: rgba(232, 228, 216, 0.18);
    --border-subtle: rgba(232, 228, 216, 0.04);
    --hairline: rgba(232, 228, 216, 0.07);

    --text-primary: #F5F1E4;
    --text-secondary: #B8B0A0;
    --text-muted: #7A7268;

    --scrollbar-track: transparent;
    --scrollbar-thumb: rgba(201, 169, 97, 0.30);

    --overlay-bg: rgba(0, 0, 0, 0.62);
    --modal-overlay: rgba(7, 12, 9, 0.78);

    --ai-gradient: linear-gradient(135deg, #122019 0%, #131A16 100%);
    --header-gradient: linear-gradient(180deg, rgba(46,107,74,0.12), rgba(11,16,14,0) 70%);

    --mesh-1: radial-gradient(45% 40% at 10% 18%, rgba(46,107,74,0.16), transparent 60%);
    --mesh-2: radial-gradient(35% 30% at 92% 12%, rgba(201,169,97,0.10), transparent 60%);
    --mesh-3: radial-gradient(55% 45% at 50% 100%, rgba(74,111,165,0.07), transparent 65%);
    --noise-opacity: 0.04;

    /* Brand surface tints */
    --tint-forest: rgba(46, 107, 74, 0.14);
    --tint-champagne: rgba(201, 169, 97, 0.12);
    --tint-live:   rgba(192, 57, 43, 0.14);
  }

  /* ───────────────────────── Light — Parchment editorial ───────────────────────── */
  [data-theme="light"] {
    --bg-primary: #F2EEE4;
    --bg-canvas: radial-gradient(140% 90% at 50% -10%, #F5F2E8 0%, #F2EEE4 50%, #E9E4D7 100%);
    --bg-card: rgba(255, 253, 247, 0.78);
    --bg-card-solid: #FBF8EF;
    --bg-surface: rgba(11, 16, 14, 0.04);
    --bg-surface-alt: #ECE6D7;
    --bg-elevated: #FFFDF7;

    --glass-bg: rgba(251, 248, 239, 0.72);
    --glass-border: rgba(11, 16, 14, 0.10);
    --glass-highlight: rgba(255, 253, 247, 0.6);

    --border-color: rgba(11, 16, 14, 0.12);
    --border-strong: rgba(11, 16, 14, 0.22);
    --border-subtle: rgba(11, 16, 14, 0.07);
    --hairline: rgba(11, 16, 14, 0.10);

    --text-primary: #0B100E;
    --text-secondary: #3A4940;
    --text-muted: #6B7268;

    --scrollbar-track: transparent;
    --scrollbar-thumb: rgba(46, 107, 74, 0.28);

    --overlay-bg: rgba(11, 16, 14, 0.30);
    --modal-overlay: rgba(11, 16, 14, 0.55);

    --ai-gradient: linear-gradient(135deg, #EFEAD9 0%, #FBF8EF 100%);
    --header-gradient: linear-gradient(180deg, rgba(46,107,74,0.06), rgba(242,238,228,0) 70%);

    --mesh-1: radial-gradient(45% 40% at 10% 18%, rgba(46,107,74,0.14), transparent 60%);
    --mesh-2: radial-gradient(35% 30% at 92% 12%, rgba(201,169,97,0.18), transparent 60%);
    --mesh-3: radial-gradient(55% 45% at 50% 100%, rgba(74,111,165,0.08), transparent 65%);
    --noise-opacity: 0.03;

    --tint-forest: rgba(46, 107, 74, 0.10);
    --tint-champagne: rgba(201, 169, 97, 0.16);
    --tint-live:   rgba(192, 57, 43, 0.10);
  }

  /* Back-compat alias names used by legacy pages */
  :root {
    --brand-pitch: var(--brand-moss);
    --brand-grass: var(--brand-forest);
    --brand-gold:  var(--brand-champagne);
    --brand-sun:   var(--brand-champagne);
    --brand-sky:   #4A6FA5;
    --brand-red:   var(--signal-live);
  }

  /* ───────────────────────── Base ───────────────────────── */
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; text-rendering: optimizeLegibility; font-feature-settings: "ss01", "ss02", "cv11"; }
  html { overflow-x: hidden; }
  body {
    font-family: var(--font-body);
    background: var(--bg-primary);
    color: var(--text-primary);
    min-height: 100vh;
    line-height: 1.55;
    overflow-x: hidden;
    transition: background-color 0.5s var(--ease-out-expo), color 0.5s var(--ease-out-expo);
  }

  /* Quieter ambient mesh */
  body::before {
    content: ""; position: fixed; inset: 0; z-index: 0; pointer-events: none;
    background: var(--bg-canvas), var(--mesh-1), var(--mesh-2), var(--mesh-3);
    background-blend-mode: normal, screen, screen, screen;
  }
  body::after {
    content: ""; position: fixed; inset: 0; z-index: 0; pointer-events: none;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.9'/%3E%3C/svg%3E");
    opacity: var(--noise-opacity); mix-blend-mode: overlay;
  }
  #root { position: relative; z-index: 1; }

  a { color: inherit; }
  button { font-family: inherit; }
  ::selection { background: var(--brand-champagne); color: var(--brand-ink); }

  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: var(--scrollbar-track); }
  ::-webkit-scrollbar-thumb { background: var(--scrollbar-thumb); border-radius: 999px; }

  /* ───────────────────────── Motion ───────────────────────── */
  @keyframes fadeIn  { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes pulse   { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:0.4; transform:scale(0.85); } }
  @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
  @keyframes liquid  { 0%,100% { border-radius: 42% 58% 47% 53% / 51% 45% 55% 49%; transform: rotate(0deg); }
                       33%    { border-radius: 58% 42% 53% 47% / 44% 58% 42% 56%; transform: rotate(120deg); }
                       66%    { border-radius: 47% 53% 42% 58% / 56% 49% 51% 44%; transform: rotate(240deg); } }
  @keyframes float   { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }

  /* ───────────────────────── Utilities ───────────────────────── */
  .cl-glass {
    background: var(--glass-bg);
    border: 1px solid var(--glass-border);
    -webkit-backdrop-filter: saturate(125%) blur(22px);
    backdrop-filter: saturate(125%) blur(22px);
  }
  .cl-serif { font-family: var(--font-display); font-feature-settings: "ss01", "lnum"; }
  .cl-italic { font-style: italic; font-variation-settings: "SOFT" 100, "WONK" 1; }
  .cl-mono { font-family: var(--font-mono); font-feature-settings: "tnum"; }
  .cl-eyebrow {
    font-size: 11px; font-weight: 600; color: var(--text-muted);
    letter-spacing: var(--tracking-eyebrow); text-transform: uppercase;
    font-feature-settings: "ss01";
  }
  .cl-rule {
    height: 1px; background: var(--hairline); width: 100%;
  }
  .cl-rule-thick {
    height: 1px; background: linear-gradient(90deg, transparent, var(--hairline) 20%, var(--hairline) 80%, transparent);
  }
  .cl-grad-text {
    /* Restrained: single warm hue, no rainbow. */
    background: linear-gradient(135deg, var(--brand-champagne) 0%, var(--brand-brass) 100%);
    -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
  }
  .cl-blob {
    position: absolute; filter: blur(72px); opacity: 0.30; pointer-events: none;
    animation: liquid 22s ease-in-out infinite, float 11s ease-in-out infinite;
  }
  .cl-shimmer {
    background: linear-gradient(90deg, transparent, rgba(232,228,216,0.10) 50%, transparent);
    background-size: 200% 100%;
    animation: shimmer 2.4s linear infinite;
  }

  /* Editorial drop-cap utility */
  .cl-dropcap::first-letter {
    font-family: var(--font-display);
    font-size: 3em;
    font-weight: 700;
    line-height: 0.9;
    float: left;
    padding: 0.05em 0.12em 0 0;
    color: var(--brand-champagne);
  }

  /* Focus ring */
  :focus-visible { outline: 1.5px solid var(--brand-forest); outline-offset: 3px; border-radius: 4px; }

  /* Native form controls: pin color-scheme to the active theme so the
     OS-rendered <option> popup, date-pickers etc. don't fall back to
     light-grey on white in dark mode. */
  [data-theme="dark"] select,
  [data-theme="dark"] input,
  [data-theme="dark"] textarea { color-scheme: dark; }
  [data-theme="light"] select,
  [data-theme="light"] input,
  [data-theme="light"] textarea { color-scheme: light; }

  /* color-scheme alone is honoured by most Chromium builds for the dropdown
     popup, but several mobile browsers (Android WebView, iOS Safari < 17.4)
     ignore it. Explicit option styling forces readable contrast on those
     engines. optgroup kept in sync for future grouped selects. */
  [data-theme="dark"] select option,
  [data-theme="dark"] select optgroup {
    background-color: var(--bg-card-solid);
    color: var(--text-primary);
  }
  [data-theme="light"] select option,
  [data-theme="light"] select optgroup {
    background-color: var(--bg-card-solid);
    color: var(--text-primary);
  }

  /* Reduced motion */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after { animation-duration: 0.001ms !important; animation-iteration-count: 1 !important; transition-duration: 0.001ms !important; }
    body::before { background: var(--bg-primary); }
    .cl-blob { animation: none; opacity: 0.15; }
  }

  /* Mobile main-content padding only. The previous attribute-selector
     override on grid-template-columns blindly collapsed every inline grid
     to a single column, breaking the runs-button bar, dashboard stat
     blocks, and anything else using inline gridTemplateColumns. Components
     that genuinely need single-column on mobile opt in via a class and
     a scoped media query (e.g. .cl-scoring-grid). */
  @media (max-width: 768px) {
    main { padding: 14px !important; }
    /* Flex items default to min-width: min-content, which lets wide tables
       and tabs grow their flex ancestor past the viewport. Force shrinking. */
    .main-content, .main-content > main { min-width: 0 !important; }
  }

  /* Narrow-only: collapse high-density KPI/stat grids (3-col, 4-col).
     Two-tier: 2 columns at <=600px, single column under 460px so cards with
     wide internal content (umpires, club roster) don't force horizontal scroll.
     minmax(0, 1fr) lets grid items shrink below their min-content width
     instead of pushing the parent wider than the viewport.
     Targets ONLY repeat(3,…) and repeat(4,…) — leaves repeat(7,1fr)
     (LiveScoring runs bar) and explicit '1fr 1fr' layouts intact. */
  @media (max-width: 600px) {
    [style*="grid-template-columns: repeat(4"],
    [style*="grid-template-columns:repeat(4"],
    [style*="grid-template-columns: repeat(3"],
    [style*="grid-template-columns:repeat(3"] {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
    }
  }
  @media (max-width: 460px) {
    [style*="grid-template-columns: repeat(4"],
    [style*="grid-template-columns:repeat(4"],
    [style*="grid-template-columns: repeat(3"],
    [style*="grid-template-columns:repeat(3"] {
      grid-template-columns: minmax(0, 1fr) !important;
    }
  }

  /* Tabs: keep flex:1 equal-width on desktop, switch to horizontally
     scrollable + nowrap pills on mobile so 6-7 tabs (My Club's "Squad,
     Practice, Info, Selection, Schedule, Analytics, Admin") cannot push
     the page wider than the viewport. */
  @media (max-width: 600px) {
    .cl-tabs {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
    }
    .cl-tabs::-webkit-scrollbar { display: none; }
    .cl-tabs > button {
      flex: 0 0 auto !important;
      white-space: nowrap;
    }
  }

  /* Data tables: wrap in a horizontally scrollable container so wide
     5+ column tables (squad roster, transfers) don't push the page
     wider than the viewport. Targets the <table> directly. */
  @media (max-width: 600px) {
    main table { display: block; overflow-x: auto; max-width: 100%; -webkit-overflow-scrolling: touch; }
  }

  /* Analytics charts grid: 2-col on desktop, 1-col stack on mobile so
     Manhattan/Worm/Partnership/WagonWheel each get the full row width
     (charts in a ~150px column become microscopic, especially for
     T40/T50 axes). Class-scoped so the explicit 1fr-1fr inline grids
     are not collapsed by the broader repeat(3..)/repeat(4..) rule. */
  @media (max-width: 600px) {
    .cl-charts-grid {
      grid-template-columns: minmax(0, 1fr) !important;
    }
  }

  /* Stadium-photo wagon wheel cards. The card has a dark-green
     gradient over a stadium aerial, so the disclosure summary chip
     needs cream foreground regardless of theme to stay readable. */
  .cl-stadium-bg > summary {
    background: rgba(0, 0, 0, 0.30) !important;
    border-color: rgba(245, 241, 228, 0.18) !important;
    color: #F5F1E4 !important;
  }
  .cl-stadium-bg > summary > span { color: inherit; }
`;
document.head.appendChild(style);

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(<React.StrictMode><App /></React.StrictMode>);
